import { create } from 'zustand'

import { captureError } from '@/utils/captureError'

import { SelectOption } from '@/lib/core/components/Select'
import { getBlockedShareFlowReason } from '@/lib/features/SmartShares/components/ShareAssetModal/utils'

import {
  PostSharesResponse,
  SelectableShareClass,
  Share,
} from '@/lib/features/SmartShares/interfaces'

export interface ShareAssetModalStore {
  isOpen: boolean
  initialOptions: Array<SelectOption>
  shareToExtend?: Share
  onwardShareToExtend?: Share
  onSuccessCallback?: () => void
  onAddNewAssetCallback?: () => void
  addedShare: PostSharesResponse | null
  showSharesSummaryModal: boolean
  shareClass: SelectableShareClass | null

  openModal: (params?: {
    initialOptions?: Array<SelectOption>
    shareToExtend?: Share
    onwardShareToExtend?: Share
    onSuccess?: () => void
    onAddNewAsset?: () => void
    shareClass?: SelectableShareClass
  }) => void

  closeModal: () => void
  setAddedShare: (share: PostSharesResponse | null) => void
  setShowSharesSummaryModal: (show: boolean) => void
  setShareClass: (shareClass: SelectableShareClass | null) => void
}

export const useShareAssetModalStore = create<ShareAssetModalStore>((set) => ({
  isOpen: false,
  initialOptions: [],
  shareToExtend: undefined,
  onwardShareToExtend: undefined,
  onSuccessCallback: undefined,
  onAddNewAssetCallback: undefined,
  addedShare: null,
  showSharesSummaryModal: false,
  shareClass: null,

  openModal: (params = {}) => {
    const {
      initialOptions = [],
      shareToExtend,
      onwardShareToExtend,
      onSuccess,
      onAddNewAsset,
      shareClass = null,
    } = params

    // Every entry point opens the modal through here, so this catches a blocked flow whatever
    // reached it. Callers disable their own actions; arriving here means one of them let it
    // through, which is a bug worth reporting rather than a state to render a form for.
    const blockedReason = getBlockedShareFlowReason({ shareToExtend, onwardShareToExtend })
    if (blockedReason) {
      captureError()(new Error(`Blocked share flow reached the modal: ${blockedReason}`))
      return
    }

    set({
      isOpen: true,
      initialOptions,
      shareToExtend,
      onwardShareToExtend,
      onSuccessCallback: onSuccess,
      onAddNewAssetCallback: onAddNewAsset,
      shareClass,
    })
  },

  closeModal: () => {
    set({
      isOpen: false,
      initialOptions: [],
      shareToExtend: undefined,
      onwardShareToExtend: undefined,
      onSuccessCallback: undefined,
      onAddNewAssetCallback: undefined,
      addedShare: null,
      showSharesSummaryModal: false,
      shareClass: null,
    })
  },

  setAddedShare: (share: PostSharesResponse | null) => {
    set({ addedShare: share })
  },

  setShowSharesSummaryModal: (show: boolean) => {
    set({ showSharesSummaryModal: show })
  },

  setShareClass: (shareClass: SelectableShareClass | null) => {
    set({ shareClass })
  },
}))
